/**
 * Agency objective-types service.
 *
 * Objective types are an agency-level strategic vocabulary ("commercial",
 * "awareness", "informative", ...) chosen when an idea is created. They are
 * shared across all clients, so they live here rather than on a client.
 *
 * The REST endpoint may not exist yet on the backend. The service surfaces a
 * normalized shape and the hooks run with `retry: false` so a missing
 * endpoint degrades to an empty list plus a visible create form rather than a
 * spinner that never resolves.
 */

import { apiClient } from "@/services/apiClient";

export interface ObjectiveType {
  id: string;
  key: string;
  label: string;
  sortOrder: number;
  isArchived: boolean;
}

export interface CreateObjectiveTypeInput {
  key?: string;
  label: string;
  sortOrder?: number;
}

export interface UpdateObjectiveTypeInput {
  label?: string;
  sortOrder?: number;
  isArchived?: boolean;
}

const BASE = "/api/objective-types";

/** Tolerate a bare array or a `{ objectiveTypes }` / `{ data }` envelope. */
function unwrapList(raw: unknown): ObjectiveType[] {
  if (Array.isArray(raw)) {
    return raw as ObjectiveType[];
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.objectiveTypes)) {
      return obj.objectiveTypes as ObjectiveType[];
    }
    if (Array.isArray(obj.data)) {
      return obj.data as ObjectiveType[];
    }
  }
  return [];
}

/** Tolerate a bare object or a `{ objectiveType }` / `{ data }` envelope. */
function unwrapOne(raw: unknown): ObjectiveType {
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (obj.objectiveType && typeof obj.objectiveType === "object") {
      return obj.objectiveType as ObjectiveType;
    }
    if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
      return obj.data as ObjectiveType;
    }
  }
  return raw as ObjectiveType;
}

export const objectiveTypesService = {
  async list(): Promise<ObjectiveType[]> {
    const raw = await apiClient.get<unknown>(BASE);
    return unwrapList(raw).sort((a, b) => a.sortOrder - b.sortOrder);
  },

  async create(input: CreateObjectiveTypeInput): Promise<ObjectiveType> {
    const raw = await apiClient.post<unknown>(BASE, input);
    return unwrapOne(raw);
  },

  async update(id: string, input: UpdateObjectiveTypeInput): Promise<ObjectiveType> {
    const raw = await apiClient.patch<unknown>(`${BASE}/${id}`, input);
    return unwrapOne(raw);
  },

  async archive(id: string): Promise<void> {
    // Backend soft-archives via DELETE /objective-types/:id (keeps historical
    // ideas readable); there is no isArchived PATCH on the API.
    await apiClient.delete(`${BASE}/${id}`);
  },
};
